/**
 * RedMart collection page: filter/sort links re-render the section in place
 * (Section Rendering API) so the phone filter sheet stays open; Load more
 * appends the next page. Without JS every link and the sort form still work.
 */

class RmCollection extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);

      const more = target.closest('[data-rm-load-more]');
      if (more instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#loadMore(more);
        return;
      }

      const link = target.closest('[data-rm-filter-link]');
      if (link instanceof HTMLAnchorElement) {
        event.preventDefault();
        const href = new URL(link.href);
        // Filter links don't carry ?view=; keep the discount view while filtering.
        const inDiscountView = new URL(window.location.href).searchParams.get('view') === 'discount';
        if (inDiscountView && !link.hasAttribute('data-rm-sort') && !href.searchParams.has('view')) href.searchParams.set('view', 'discount');
        this.#update(href.href);
      }
    });

    this.addEventListener('change', (event) => {
      const select = /** @type {HTMLElement} */ (event.target);
      const form = select.closest('[data-rm-sort-form]');
      if (!(form instanceof HTMLFormElement)) return;
      const url = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of new FormData(form)) url.searchParams.append(key, String(value));
      // "Biggest discount" is the collection.discount view, not a Shopify sort value.
      if (url.searchParams.get('sort_by') === 'discount') {
        url.searchParams.delete('sort_by');
        url.searchParams.set('view', 'discount');
      }
      this.#update(url.href);
    });

    window.addEventListener('popstate', () => this.#update(window.location.href, false));
  }

  /** @param {string} href */
  async #fetchSection(href) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');
    const response = await fetch(url, { signal: this.#controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return html.querySelector('rm-collection');
  }

  /**
   * @param {string} href
   * @param {boolean} [push]
   */
  async #update(href, push = true) {
    const sheet = this.querySelector('#RmFilters');
    const sheetOpen = sheet instanceof HTMLDialogElement && sheet.open;
    const scroll = sheet?.querySelector('.rm-filters__body')?.scrollTop ?? 0;
    this.setAttribute('aria-busy', 'true');

    try {
      const fresh = await this.#fetchSection(href);
      if (!fresh) throw new Error('section missing');
      if (sheetOpen) sheet.close();
      this.replaceChildren(...fresh.childNodes);
      if (push) {
        const clean = new URL(href, window.location.origin);
        clean.searchParams.delete('section_id');
        window.history.pushState({}, '', clean);
      }
      const next = this.querySelector('#RmFilters');
      if (sheetOpen && next instanceof HTMLDialogElement) {
        next.classList.add('is-restored');
        next.showModal();
        const body = next.querySelector('.rm-filters__body');
        if (body) body.scrollTop = scroll;
      }
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {HTMLAnchorElement} button */
  async #loadMore(button) {
    button.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.#fetchSection(button.href);
      const grid = this.querySelector('[data-rm-grid]');
      const freshGrid = fresh?.querySelector('[data-rm-grid]');
      if (!grid || !freshGrid) throw new Error('grid missing');
      grid.append(...freshGrid.childNodes);
      this.querySelector('[data-rm-more]')?.replaceWith(fresh.querySelector('[data-rm-more]') ?? '');
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = button.href;
    }
  }
}

if (!customElements.get('rm-collection')) customElements.define('rm-collection', RmCollection);
